import { useCallback, useRef, useState } from "react";

/**
 * Drives a fake-but-informative step progress animation for long-running API calls.
 *
 * @param {Array<{label: string, duration: number}>} steps
 *   Each step has a label and a duration in ms before advancing to the next step.
 *   The last step's duration is ignored — it stays visible until finish() is called.
 *
 * Usage:
 *   const progress = useProgressSteps([
 *     { label: "Parsing resume", duration: 3500 },
 *     { label: "Scoring keywords", duration: 5000 },
 *     { label: "Rewriting sections", duration: 0 }, // last step — stays until API responds
 *   ]);
 *
 *   // on button click:
 *   progress.start();
 *   try { await api.post(...); } finally { progress.finish(); }
 *
 *   // in JSX:
 *   {progress.isRunning && <ProgressSteps steps={steps} {...progress} />}
 */
export function useProgressSteps(steps) {
  const [currentStep, setCurrentStep] = useState(-1);
  const timersRef = useRef([]);

  const start = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setCurrentStep(0);
    let cumulative = 0;
    for (let i = 1; i < steps.length; i++) {
      cumulative += steps[i - 1].duration;
      const idx = i;
      timersRef.current.push(setTimeout(() => setCurrentStep(idx), cumulative));
    }
  }, [steps]);

  const finish = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setCurrentStep(-1);
  }, []);

  const pct = currentStep < 0 ? 0 : Math.round(((currentStep + 1) / steps.length) * 100);

  return { currentStep, isRunning: currentStep >= 0, pct, start, finish };
}
